import { User, UserRole, LoginForm, RegisterForm } from '../types/user';
import { useStore } from '../store';

// 初始化用户数据
const initializeUsers = () => {
  const storedUsers = localStorage.getItem('users');
  if (!storedUsers) {
    const initialUsers: User[] = [
      {
        id: '1',
        username: 'superadmin',
        email: 'superadmin@example.com',
        password: 'hashed_password', // 实际应该使用加密后的密码
        role: UserRole.SUPER_ADMIN,
        createdAt: new Date(),
        updatedAt: new Date(),
      },
    ];
    localStorage.setItem('users', JSON.stringify(initialUsers));
    return initialUsers;
  }
  return JSON.parse(storedUsers);
};

// 用户数据
let users: User[] = initializeUsers();

// 保存用户数据到 localStorage
const saveUsers = () => {
  localStorage.setItem('users', JSON.stringify(users));
};

// 获取当前登录用户
export const getCurrentUser = (): User | null => {
  const userJson = localStorage.getItem('currentUser');
  return userJson ? JSON.parse(userJson) : null;
};

// 检查用户是否有权限
export const hasPermission = (requiredRole: UserRole): boolean => {
  const currentUser = getCurrentUser();
  if (!currentUser) return false;

  const roleHierarchy = {
    [UserRole.SUPER_ADMIN]: 3,
    [UserRole.ADMIN]: 2,
    [UserRole.USER]: 1,
  };

  return roleHierarchy[currentUser.role] >= roleHierarchy[requiredRole];
};

// 用户登录
export const login = async (loginForm: LoginForm): Promise<User> => {
  const user = users.find(u => u.email === loginForm.email && u.password === loginForm.password);
  if (!user) {
    throw new Error('邮箱或密码错误');
  }
  
  localStorage.setItem('currentUser', JSON.stringify(user));
  useStore.getState().setCurrentUser(user);
  return user;
};

// 用户注册
export const register = async (registerForm: RegisterForm): Promise<User> => {
  // 检查邮箱是否已存在
  if (users.some(u => u.email === registerForm.email)) {
    throw new Error('Email already exists');
  }

  const newUser: User = {
    id: String(users.length + 1),
    username: registerForm.username,
    email: registerForm.email,
    password: registerForm.password, // 实际应该加密存储
    role: UserRole.USER, // 默认为普通用户
    createdAt: new Date(),
    updatedAt: new Date(),
  };

  users.push(newUser);
  saveUsers(); // 保存到 localStorage
  return newUser;
};

// 设置用户角色（仅超级管理员可用）
export const setUserRole = (userId: string, newRole: UserRole): boolean => {
  const currentUser = getCurrentUser();
  if (!currentUser || currentUser.role !== UserRole.SUPER_ADMIN) {
    throw new Error('Permission denied');
  }

  const userToUpdate = users.find(u => u.id === userId);
  if (!userToUpdate) {
    throw new Error('User not found');
  }

  userToUpdate.role = newRole;
  userToUpdate.updatedAt = new Date();
  saveUsers(); // 保存到 localStorage
  return true;
};

// 退出登录
export const logout = (): void => {
  localStorage.removeItem('currentUser');
  useStore.getState().clearAll();
};

// 删除用户（仅超级管理员可用）
export const deleteUser = (userId: string): boolean => {
  const currentUser = getCurrentUser();
  if (!currentUser || currentUser.role !== UserRole.SUPER_ADMIN) {
    throw new Error('权限不足');
  }

  const userToDelete = users.find(u => u.id === userId);
  if (!userToDelete) {
    throw new Error('用户不存在');
  }

  // 不允许删除自己
  if (userToDelete.id === currentUser.id) {
    throw new Error('不能删除当前登录的账户');
  }

  // 从用户列表中删除用户
  users = users.filter(u => u.id !== userId);
  saveUsers(); // 保存到 localStorage
  return true;
};

// 修改密码
export const changePassword = async (userId: string, currentPassword: string, newPassword: string): Promise<boolean> => {
  const user = users.find(u => u.id === userId);
  if (!user) {
    throw new Error('用户不存在');
  }

  // 验证当前密码
  if (user.password !== currentPassword) {
    throw new Error('当前密码错误');
  }

  // 更新密码
  user.password = newPassword;
  user.updatedAt = new Date();
  saveUsers();

  // 更新当前登录用户信息
  const currentUser = getCurrentUser();
  if (currentUser && currentUser.id === userId) {
    localStorage.setItem('currentUser', JSON.stringify(user));
    useStore.getState().setCurrentUser(user);
  }

  return true;
}; 